import type { RankedSpace } from "../types";

/**
 * Space (building) photos are a building-level attribute: every room in the same
 * building shares the same `spacePhotos`. Rooms reopened from a saved proposal may
 * lack `spacePhotos` (the field was not persisted / not backfilled), while a sibling
 * room from the live search — same building — carries them. These helpers let the UI
 * borrow a sibling's space photos so the "Espaço" section shows consistently.
 *
 * Building identity is keyed by `partnerId` (the space/building id) with a fallback
 * to `spaceName`, since integration sources may not always provide a numeric id.
 */

function buildingKeys(
  space: Pick<RankedSpace, "partnerId" | "spaceName">,
): string[] {
  const keys: string[] = [];
  if (space.partnerId != null) keys.push(`id:${space.partnerId}`);
  if (space.spaceName) keys.push(`name:${space.spaceName}`);
  return keys;
}

/**
 * Index the first non-empty `spacePhotos` found per building across the given spaces.
 * Both the partnerId-based and name-based keys point to the same photo array.
 */
export function buildSpacePhotosByBuilding(
  spaces: RankedSpace[],
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const space of spaces) {
    const photos = space.spacePhotos ?? [];
    if (photos.length === 0) continue;
    for (const key of buildingKeys(space)) {
      if (!map.has(key)) map.set(key, photos);
    }
  }
  return map;
}

/**
 * Resolve a room's space photos: prefer its own, otherwise borrow from a sibling
 * room of the same building. Returns an empty array when nothing is available.
 */
export function resolveSpacePhotos(
  space: RankedSpace | null | undefined,
  byBuilding: Map<string, string[]>,
): string[] {
  if (!space) return [];
  if ((space.spacePhotos?.length ?? 0) > 0) return space.spacePhotos!;
  for (const key of buildingKeys(space)) {
    const siblingPhotos = byBuilding.get(key);
    if (siblingPhotos && siblingPhotos.length > 0) return siblingPhotos;
  }
  return [];
}

/**
 * The single canonical key identifying a room's building — `id:<partnerId>` when
 * a numeric space id exists, otherwise `name:<spaceName>`. Use this to key
 * per-building state (building photos are shared by all rooms in the building).
 * Rooms with neither id nor name collapse to a stable `""` bucket.
 */
export function buildingKeyOf(
  space: Pick<RankedSpace, "partnerId" | "spaceName">,
): string {
  return buildingKeys(space)[0] ?? "";
}

/** A building and the selected rooms that belong to it, in display order. */
export interface BuildingGroup {
  key: string;
  spaceName: string;
  rooms: RankedSpace[];
}

/**
 * Group rooms by their building, preserving the incoming order. Each building
 * group is anchored at the position of its FIRST room; later rooms of the same
 * building are absorbed into that group (so the rooms of one building always
 * render together as one card). Keyed by {@link buildingKeyOf}.
 */
export function groupRoomsByBuilding(spaces: RankedSpace[]): BuildingGroup[] {
  const groups: BuildingGroup[] = [];
  const indexByKey = new Map<string, number>();
  for (const space of spaces) {
    const key = buildingKeyOf(space);
    const existing = indexByKey.get(key);
    if (existing == null) {
      indexByKey.set(key, groups.length);
      groups.push({ key, spaceName: space.spaceName, rooms: [space] });
    } else {
      groups[existing].rooms.push(space);
    }
  }
  return groups;
}
